import React, { useCallback, useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  SurveyAnsweringForm,
  type SurveyFormBuilderElement,
  type SurveyFormSubmitResult,
  type SurveySubmitAnswers,
} from '@factorialco/f0-react'
import type { F0TimelineRowAction } from '@factorialco/f0-react'
import type { ModuleId } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'

import {
  PurchaseRequestDetailDocument,
  VendorOnboardingTaskMyMembershipDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FromQuestionnaireToForm } from 'modules/forms/components/newFormEditor/helpers'
import { useAnswerForm } from 'modules/forms/hooks/useAnswerForm'
import { useLoadForm } from 'modules/forms/hooks/useForms'
import {
  applySurveySubmitAnswersToForm,
  mapFormToQuestionnaire,
} from 'modules/forms/lib/surveyAnsweringFormHelpers'

const PROCUREMENT_MODULE_ID: ModuleId = 'finance-spending'

interface TimelineFormActionsRenderProps {
  primaryAction: F0TimelineRowAction
}

interface TimelineFormActionsProps {
  formId: string
  materializedFormId: string
  autoOpen?: boolean
  onClose?: () => void
  onFormCompleted?: () => void
  children: (props: TimelineFormActionsRenderProps) => React.ReactNode
}

const TimelineFormActions = ({
  formId,
  materializedFormId,
  autoOpen,
  onClose,
  onFormCompleted,
  children,
}: TimelineFormActionsProps) => {
  const { access } = useCurrentSession()
  const apolloClient = useApolloClient()
  const [isFormOpen, setIsFormOpen] = useState(autoOpen ?? false)
  const [isCompleted, setIsCompleted] = useState(false)

  const { data: membershipData } = useQuery(VendorOnboardingTaskMyMembershipDocument, {
    variables: {
      materializedFormId,
      respondentId: access.id,
    },
  })
  const membershipId = membershipData?.forms?.memberships?.[0]?.id ?? undefined

  const { form, loadingForm } = useLoadForm({
    formId,
    resourceType: 'Procurement::TaskForm',
  })

  const elements = useMemo<SurveyFormBuilderElement[]>(() => {
    if (!form) return []
    return FromQuestionnaireToForm(mapFormToQuestionnaire(form).elements)
  }, [form])

  const { saveAnswers } = useAnswerForm()

  const handleClose = useCallback(() => {
    setIsFormOpen(false)
    onClose?.()
  }, [onClose])

  const handleSubmit = useCallback(
    async (answers: SurveySubmitAnswers): Promise<SurveyFormSubmitResult> => {
      if (isCompleted) {
        return {
          success: false,
          errors: {
            form: i18n.t('tasks.types.procurement_complete_form.already_completed'),
          },
        }
      }

      if (!form || !membershipId) {
        return { success: false, errors: { form: i18n.t('common.error') } }
      }

      applySurveySubmitAnswersToForm(form, answers)

      const saved = await saveAnswers({
        form,
        membershipId,
        onSuccess: () => {
          form?.resetFormAnswers()
          handleClose()
          setIsCompleted(true)
          onFormCompleted?.()
          apolloClient.refetchQueries({ include: [PurchaseRequestDetailDocument] })
          app.setFlash({
            type: 'correct',
            message: i18n.t('tasks.responses.mark_as_done'),
          })
        },
      })

      if (!saved) {
        return { success: false }
      }

      return { success: true }
    },
    [form, membershipId, saveAnswers, isCompleted, handleClose]
  )

  const primaryAction: F0TimelineRowAction = {
    label: i18n.t('procurement.purchase_requests.detail.actions.complete_form'),
    icon: Pencil,
    onClick: () => setIsFormOpen(true),
    disabled: isCompleted,
  }

  return (
    <>
      {children({ primaryAction })}

      {isFormOpen && (
        <SurveyAnsweringForm
          elements={elements}
          mode="all-questions"
          title={form?.title ?? ''}
          description={form?.description ?? undefined}
          isOpen={isFormOpen}
          onClose={handleClose}
          loading={loadingForm}
          position="fullscreen"
          onSubmit={handleSubmit}
          module={{
            id: PROCUREMENT_MODULE_ID,
            label: i18n.t('sidebar.finance_spending'),
            href: '/procurement',
          }}
          labels={{
            empty: {
              title: i18n.t('f0.survey_answering_form.labels.empty.title'),
              description: i18n.t('f0.survey_answering_form.labels.empty.description'),
              emoji: '📝',
            },
          }}
        />
      )}
    </>
  )
}

export default TimelineFormActions
